import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, User, ChevronDown } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import MakebaLogo from "./MakebaLogo";

const shopSubs = [
  { to: "/shop", label: "All Products" },
  { to: "/shop?category=backpacks", label: "Backpacks" },
  { to: "/shop?category=travel", label: "Travel" },
  { to: "/shop?category=hbcu", label: "HBCU" },
  { to: "/shop?category=tshirts", label: "T-Shirts" },
  { to: "/shop?category=embroidery", label: "Embroidery" },
  { to: "/shop?category=kids", label: "Kids & Puzzles" },
];

const externalCollections = [];

const primaryLinks = [
  { to: "/homecoming", label: "Homecoming", accent: true, flag: "homecoming_enabled" },
  { to: "/steam", label: "STEAM Kits" },
  { to: "/bulk", label: "Custom Apparel" },
  { to: "/gift-cards", label: "Gift Cards" },
  { to: "/about", label: "About" },
  { to: "/designer", label: "Design Studio" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const { count } = useCart();
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [shopOpenMobile, setShopOpenMobile] = React.useState(true);
  const [config, setConfig] = React.useState({});
  const loc = useLocation();

  React.useEffect(() => {
    api.get("/site-config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const navLinks = primaryLinks.filter((l) => !l.flag || config[l.flag]);

  const accountHref = user && user.role === "customer" ? "/account" : "/login";
  const isShopActive = loc.pathname === "/shop";

  return (
    <>
      {/* Announcement bar */}
      <div data-testid="announcement-bar" className="bg-black text-white text-center text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.22em] uppercase py-1.5 sm:py-2 font-medium px-3">
        <span className="text-[#F3E30A]">✦ 10% OFF YOUR FIRST ORDER</span> · Use code <strong className="text-[#F3E30A]">WELCOME10</strong> at checkout ·{" "}
        <Link to="/shop" className="underline underline-offset-4 hover:text-[#F3E30A]">Shop now →</Link>
      </div>

      <header
        data-testid="site-navbar"
        className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#E5E2DA]"
      >
        <div className="container-x py-2 lg:py-4 flex items-center gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <MakebaLogo className="h-6 lg:h-9 w-auto" tagline={true} />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 ml-12">
            {/* Shop dropdown */}
            <div className="relative group">
              <Link
                to="/shop"
                data-testid="nav-shop"
                className={`inline-flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase font-semibold transition ${
                  isShopActive ? "text-[#EC008C]" : "text-black group-hover:text-[#EC008C]"
                }`}
              >
                Shop <ChevronDown size={12} className="mt-0.5" />
              </Link>
              <div
                data-testid="nav-shop-dropdown"
                className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 min-w-[220px] z-50"
              >
                <div className="bg-white border-2 border-black shadow-xl py-2">
                  {shopSubs.map((s) => (
                    <Link
                      key={s.to + s.label}
                      to={s.to}
                      data-testid={`nav-shop-sub-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                      className="block px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold text-black hover:bg-[#F3E30A] transition-colors"
                    >
                      {s.label}
                    </Link>
                  ))}
                  <div className="border-t border-black/15 my-1" />
                  {externalCollections.map((s) => (
                    <a
                      key={s.href}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`nav-shop-sub-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                      className="flex items-center justify-between px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#EC008C] hover:bg-[#F3E30A] hover:text-black transition-colors"
                    >
                      {s.label}
                      <span className="ml-2 text-[10px]">↗</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Other primary links */}
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[11px] tracking-[0.2em] uppercase transition ${
                  l.accent ? "font-black text-[#F58221] hover:text-black" :
                  loc.pathname === l.to ? "text-[#EC008C] font-semibold" : "text-black hover:text-[#EC008C] font-semibold"
                }`}
              >
                {l.label}
              </Link>
            ))}

            {/* Auth text link (desktop) */}
            <Link
              to={accountHref}
              data-testid="nav-signin"
              className={`text-[11px] tracking-[0.2em] uppercase font-semibold transition ${
                loc.pathname === accountHref ? "text-[#EC008C]" : "text-black hover:text-[#EC008C]"
              }`}
            >
              {user ? "My Account" : "Sign In / Register"}
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-0.5 lg:gap-1">
            <Link
              to="/shop"
              data-testid="nav-search"
              className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center text-black hover:text-[#EC008C]"
              title="Search products"
            >
              <Search size={16} className="lg:hidden" />
              <Search size={18} className="hidden lg:block" />
            </Link>
            <Link
              to={accountHref}
              data-testid="nav-account"
              className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center text-black hover:text-[#EC008C]"
              title={user ? `Hi, ${user.name || user.email.split('@')[0]}` : "Sign in / Register"}
            >
              <User size={16} className="lg:hidden" />
              <User size={18} className="hidden lg:block" />
            </Link>
            <Link
              to="/cart"
              data-testid="nav-cart"
              className="relative w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center text-black hover:text-[#EC008C]"
            >
              <ShoppingBag size={16} className="lg:hidden" />
              <ShoppingBag size={18} className="hidden lg:block" />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -top-0.5 -right-0.5 bg-[#EC008C] text-white text-[9px] lg:text-[10px] font-bold rounded-full w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-8 h-8 flex items-center justify-center"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#E5E2DA] bg-white px-5 py-4 flex flex-col gap-1">
            {/* Shop group with expandable subs */}
            <button
              type="button"
              onClick={() => setShopOpenMobile((v) => !v)}
              data-testid="mobile-nav-shop-toggle"
              className="flex items-center justify-between px-4 py-3 text-[13px] tracking-[0.2em] uppercase font-semibold text-black hover:text-[#EC008C] border-b border-[#F0EEE9]"
            >
              Shop
              <ChevronDown size={14} className={`transition-transform ${shopOpenMobile ? "rotate-180" : ""}`} />
            </button>
            {shopOpenMobile && (
              <div className="pl-3 border-l-2 border-[#EC008C] ml-4 mb-2">
                {shopSubs.map((s) => (
                  <Link
                    key={s.to + s.label}
                    to={s.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-shop-sub-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    className="block px-4 py-2.5 text-[12px] tracking-[0.2em] uppercase font-semibold text-black hover:text-[#EC008C]"
                  >
                    {s.label}
                  </Link>
                ))}
                {externalCollections.map((s) => (
                  <a
                    key={s.href}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-shop-sub-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    className="block px-4 py-2.5 text-[12px] tracking-[0.2em] uppercase font-semibold text-[#EC008C] hover:text-black"
                  >
                    {s.label} ↗
                  </a>
                ))}
              </div>
            )}

            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-3 text-[13px] tracking-[0.2em] uppercase font-semibold text-black hover:text-[#EC008C] border-b border-[#F0EEE9]"
              >
                {l.label}
              </Link>
            ))}

            <Link
              to={accountHref}
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-account"
              className="px-4 py-3 text-[13px] tracking-[0.2em] uppercase font-semibold text-black hover:text-[#EC008C] border-b border-[#F0EEE9]"
            >
              {user ? "My Account" : "Sign In / Register"}
            </Link>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
